import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  MaintenanceState,
  MaintenanceStatus,
  UpdateMaintenanceInput,
} from '@catalyst-psa/shared';

import { api } from '../../api/client.js';

const maintenanceKeys = {
  status: ['maintenance', 'status'] as const,
  state: ['maintenance', 'state'] as const,
};

/** Public maintenance status, polled by every signed-in client to drive the overlay. */
export function useMaintenanceStatus(): UseQueryResult<MaintenanceStatus> {
  return useQuery({
    queryKey: maintenanceKeys.status,
    queryFn: () => api.get<MaintenanceStatus>('/maintenance/status'),
    refetchInterval: 30_000,
    refetchIntervalInBackground: true,
    staleTime: 0,
  });
}

/** Full admin view of the maintenance state for the admin toggle page. */
export function useMaintenanceState(enabled = true): UseQueryResult<MaintenanceState> {
  return useQuery({
    queryKey: maintenanceKeys.state,
    queryFn: () => api.get<MaintenanceState>('/maintenance'),
    enabled,
  });
}

export function useUpdateMaintenance(): UseMutationResult<
  MaintenanceState,
  unknown,
  UpdateMaintenanceInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateMaintenanceInput) => api.put<MaintenanceState>('/maintenance', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: maintenanceKeys.state });
      void queryClient.invalidateQueries({ queryKey: maintenanceKeys.status });
    },
  });
}
